import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClientService } from '../../../core/services/http-client.service';
import { CommonService } from '../../../core/services/common.service';
import { environment } from '../../../../environments/environment';

/**
 * Hosts the Diwali Unlimited Dhamaka design mockup (Main.dc.html) end-to-end,
 * with real SmartDukaan backend plugged in via a postMessage bridge.
 *
 * The design is served as-is from /assets/festive-social-media-offer-2/index.html
 * — it owns the React runtime, state machine, animations, audio and canvas FX.
 * The iframe's sendOtp / verifyOtp / burst methods were patched to call
 * `window.parent.postMessage({ __sd: 1, type: '…', reqId, payload }, '*')`
 * instead of their demo rules. This component listens for those messages and
 * dispatches to the real APIs:
 *
 *   otp:send     → store/otp/generateOTP
 *   otp:verify   → store/signinWithOtp (+ hydrate customer + fetch my-coupon)
 *   burst:do     → store/diwali/burst
 *
 * The response is posted back to the iframe with `{ __sd: 1, replyTo: reqId,
 * ok: true|false, data | error }`. All auth / CORS / env resolution stays in
 * Angular's HttpClient, so the iframe stays unaware of API URLs or tokens.
 */
@Component({
  selector: 'app-festive-social-media-offer-2',
  templateUrl: './festive-social-media-offer-2.component.html',
  styleUrls: ['./festive-social-media-offer-2.component.css'],
})
export class FestiveSocialMediaOffer2Component implements OnInit, OnDestroy {
  readonly src: SafeResourceUrl;
  private readonly onMessage = (ev: MessageEvent) => this.handleIframeMessage(ev);
  /** Mobile the user authenticated with — kept so burst:do can echo it. */
  private authedMobile = '';

  constructor(
    sanitizer: DomSanitizer,
    private http: HttpClientService,
    private common: CommonService,
  ) {
    this.src = sanitizer.bypassSecurityTrustResourceUrl(
      '/assets/festive-social-media-offer-2/index.html'
    );
  }

  ngOnInit(): void {
    window.addEventListener('message', this.onMessage);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
  }

  /** Entry point for every postMessage the iframe fires. */
  private handleIframeMessage(ev: MessageEvent): void {
    const msg = ev && ev.data;
    if (!msg || msg.__sd !== 1 || !msg.reqId || !msg.type) return;

    switch (msg.type) {
      case 'auth:probe':
        return this.handleAuthProbe(msg.reqId);
      case 'auth:logout':
        return this.handleAuthLogout(msg.reqId);
      case 'otp:send':
        return this.handleOtpSend(msg.reqId, msg.payload || {});
      case 'otp:verify':
        return this.handleOtpVerify(msg.reqId, msg.payload || {});
      case 'burst:do':
        return this.handleBurstDo(msg.reqId, msg.payload || {});
      default:
        this.reply(msg.reqId, false, null, 'unknown_type');
    }
  }

  /**
   * Called by the iframe on mount. If a Diwali-authenticated mobile (or main-sdstore
   * customer) is already in localStorage, the iframe skips the login modal. Response is
   * shaped like otp:verify so the iframe reuses the same post-verify path.
   */
  private handleAuthProbe(reqId: string): void {
    const authedMobile = this.readAuthedMobile();
    if (!authedMobile) { this.reply(reqId, true, { authed: false }); return; }
    this.authedMobile = authedMobile;
    this.http.get('store/diwali/myCoupon', { mobile: authedMobile }).subscribe(
      (res: any) => {
        const payload = res?.response || res?.data || res;
        const coupon = payload && payload.id ? payload : null;
        this.reply(reqId, true, {
          authed: true,
          mobile: authedMobile,
          coupon: coupon ? this.shapeCoupon(coupon) : null,
          billed: !!payload?.billed,
          storeRetry: !!payload?.storeRetry,
          claimed: !!payload?.claimed,
        });
      },
      () => this.reply(reqId, true, { authed: true, mobile: authedMobile, coupon: null }),
    );
  }

  /** Iframe tells us the user tapped "Log out" — nuke the whole session. */
  private handleAuthLogout(reqId: string): void {
    try {
      this.common.setLoggedIn(false);
      this.common.removeAllStorage();
    } catch { /* localStorage unavailable */ }
    this.authedMobile = '';
    this.reply(reqId, true, { cleared: true });
  }

  /** Resolve "which mobile is already authenticated in this browser" or '' if none. */
  private readAuthedMobile(): string {
    try {
      const loggedIn = this.common.isLoggedIn();
      const diwali = (localStorage.getItem('sd_diwali_authenticated_mobile') || '').trim();
      if (diwali && /^[6-9]\d{9}$/.test(diwali)) return diwali;
      if (loggedIn) {
        const customer = this.common.getStorage('customer') || this.common.getData();
        const m: string = (customer?.mobileNumber || customer?.mobile || '').trim();
        if (/^[6-9]\d{9}$/.test(m)) return m;
      }
    } catch { /* localStorage unavailable */ }
    return '';
  }

  private handleOtpSend(reqId: string, payload: { mobile?: string }): void {
    const mobile = String(payload.mobile || '').replace(/\s+/g, '');
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      this.reply(reqId, false, null, 'bad_mobile');
      return;
    }
    // SMS-delivered OTP via the regular login endpoint. Earlier the Diwali flow routed OTP
    // through email (OtpProcessor.generateOtpViaEmail) because SMS reach was patchy; Nexg
    // has since approved a fresh DLT template so SMS is reliable again. Email entry is
    // removed from the modal to keep the login identical to the rest of the sdstore app.
    this.http.get('store/otp/generateOTP', { mobile }).subscribe(
      (data: any) => {
        const refId = data?.response?.reference_id || data?.reference_id || '';
        this.reply(reqId, true, { referenceId: refId });
      },
      () => this.reply(reqId, false, null, 'net'),
    );
  }

  private handleOtpVerify(reqId: string, payload: { mobile?: string; otp?: string; referenceId?: string }): void {
    const mobile = String(payload.mobile || '').replace(/\s+/g, '');
    const otp = String(payload.otp || '').trim();
    const referenceId = String(payload.referenceId || '');
    if (!/^[6-9]\d{9}$/.test(mobile) || !/^\d{4,6}$/.test(otp)) {
      this.reply(reqId, false, null, 'bad_input');
      return;
    }
    this.http.post('store/signinWithOtp', { mobile, otp, referenceId }).subscribe(
      () => this.hydrateAndFetchCoupon(reqId, mobile),
      (err: any) => {
        const msg = err?.error?.message || err?.message || 'wrong_otp';
        this.reply(reqId, false, null, msg);
      },
    );
  }

  /**
   * OTP verified — attach the customer record if one exists, remember the authed
   * mobile for burst:do, then look up any existing online coupon so the iframe
   * can jump straight to the won card for a returning visitor.
   */
  private hydrateAndFetchCoupon(reqId: string, mobile: string): void {
    // "Last login wins" — if a different mobile is already authenticated in this browser,
    // nuke the full session before installing the new one. No-op when the same mobile
    // logs in again (so returning visitors don't lose their Diwali state).
    this.clearStaleSessionIfMobileChanged(mobile);
    this.authedMobile = mobile;
    this.http.get('store/checkmobile/' + mobile).subscribe(
      (data: any) => {
        const customer = data?.response?.exists && data.response.customer;
        if (customer && customer.id) {
          this.common.setStorage('customer', customer);
          this.common.setLoggedIn(true);
        }
        try { localStorage.setItem('sd_diwali_authenticated_mobile', mobile); } catch { /* best-effort */ }
        this.fetchExistingCoupon(reqId, mobile);
      },
      () => {
        try { localStorage.setItem('sd_diwali_authenticated_mobile', mobile); } catch { /* best-effort */ }
        this.fetchExistingCoupon(reqId, mobile);
      },
    );
  }

  /**
   * Clear the stored customer / token / cart / Diwali mobile when the newly-authenticated
   * mobile differs from whatever was there before. Same semantics as the version in
   * FestiveStoreOffer2Component — kept duplicated rather than extracted to a shared service
   * so each page owns its own login lifecycle and we don't accidentally wire a dependency
   * into the main sdstore login flow.
   */
  private clearStaleSessionIfMobileChanged(newMobile: string): void {
    try {
      const existing = this.common.getStorage('customer') || this.common.getData();
      const existingMobile: string | undefined = existing?.mobileNumber || existing?.mobile;
      const existingDiwali = localStorage.getItem('sd_diwali_authenticated_mobile');
      const differentCustomer = !!existingMobile && existingMobile !== newMobile;
      const differentDiwali = !!existingDiwali && existingDiwali !== newMobile;
      if (differentCustomer || differentDiwali) {
        this.common.setLoggedIn(false);
        this.common.removeAllStorage();
      }
    } catch { /* localStorage unavailable */ }
  }

  private fetchExistingCoupon(reqId: string, mobile: string): void {
    this.http.get('store/diwali/myCoupon', { mobile }).subscribe(
      (res: any) => {
        const payload = res?.response || res?.data || res;
        const coupon = payload && payload.id ? payload : null;
        this.reply(reqId, true, {
          mobile,
          // Pass through regardless of status — the iframe decides what to render based on
          // coupon.status (GENERATED → win card, REDEEMED/OVERRIDDEN → claimed card with
          // finalGift, EXPIRED → offer-over nudge).
          coupon: coupon ? this.shapeCoupon(coupon) : null,
          billed: !!payload?.billed,
          storeRetry: !!payload?.storeRetry,
          claimed: !!payload?.claimed,
        });
      },
      () => this.reply(reqId, true, { mobile, coupon: null }),
    );
  }

  private handleBurstDo(reqId: string, payload: { mobile?: string }): void {
    const mobile = String(payload.mobile || this.authedMobile || '').replace(/\s+/g, '');
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      this.reply(reqId, false, null, 'bad_mobile');
      return;
    }
    this.http.post('store/diwali/burst', { mobile }).subscribe(
      (res: any) => {
        const data = res?.data || res?.response || res;
        const coupon = data && data.id ? data : null;
        if (!coupon) { this.reply(reqId, false, null, 'burst_failed'); return; }
        this.reply(reqId, true, { coupon: this.shapeCoupon(coupon) });
      },
      (err: any) => {
        const msg = err?.error?.message || err?.message || 'burst_failed';
        this.reply(reqId, false, null, msg);
      },
    );
  }

  /** Return only the subset the iframe actually reads. `thumbnailFullUrl` is the resolved
      doc URL (environment.IMG_URL + id). finalGift (REDEEMED/OVERRIDDEN only) carries the
      gift the customer actually walked away with after the merge, keyed to the scratch_offer
      row — resolved on the backend via redeemedScratchOfferId. */
  private shapeCoupon(c: any): any {
    const gift = c.gift ? {
      id: c.gift.id,
      name: c.gift.name,
      thumbnailUrl: c.gift.thumbnailUrl || null,
      thumbnailFullUrl: this.resolveDocUrl(c.gift.thumbnailUrl),
    } : null;
    const finalGift = c.finalGift ? {
      scratchOfferId: c.finalGift.scratchOfferId,
      invoiceNumber: c.finalGift.invoiceNumber || null,
      giftId: c.finalGift.giftId,
      scratched: !!c.finalGift.scratched,
      gift: c.finalGift.gift ? {
        id: c.finalGift.gift.id,
        name: c.finalGift.gift.name,
        thumbnailUrl: c.finalGift.gift.thumbnailUrl || null,
        thumbnailFullUrl: this.resolveDocUrl(c.finalGift.gift.thumbnailUrl),
      } : null,
    } : null;
    return {
      id: c.id,
      couponCode: c.couponCode,
      patakhaType: c.patakhaType,
      status: c.status,
      redeemedScratchOfferId: c.redeemedScratchOfferId || null,
      gift,
      finalGift,
    };
  }

  /**
   * Resolve a gift's `thumbnail_url` (either a numeric document id, a relative path, or an
   * already-qualified URL) to a fully-qualified image URL the iframe can drop into <img src=…>.
   * Mirrors the shape of festive-social-media-offer.component.ts#giftThumb but reads the
   * environment base so dev loads from localhost:8080/document/… instead of prod.
   */
  private resolveDocUrl(raw: any): string | null {
    const url = typeof raw === 'string' ? raw.trim() : raw != null ? String(raw) : '';
    if (!url) return null;
    if (/^https?:/i.test(url)) return url;
    const base = ('https://apis.smartdukaan.com/document/').replace(/\/?$/, '/');
    return base + url;
  }

  /** Post the reply back into the iframe. The iframe matches on `replyTo === reqId`. */
  private reply(reqId: string, ok: boolean, data: any, error?: string): void {
    const iframe = document.querySelector('iframe.fsmo2-frame') as HTMLIFrameElement | null;
    const win = iframe && iframe.contentWindow;
    if (!win) return;
    try {
      win.postMessage({ __sd: 1, replyTo: reqId, ok, data, error }, '*');
    } catch { /* iframe may have navigated away — nothing to do */ }
  }
}
